import { useEffect, useState } from 'react'
import type { Diamond } from '../../api/types'
import { canUseAgenticSourcing } from '../../lib/sourcing'
import {
  addToSourcing,
  removeFromSourcing,
  isInSourcing,
  subscribeSourcing,
} from '../../lib/sourcingList'
import { stoneKey } from '../../lib/stoneKey'
import { feedback } from '../../lib/feedback'

/**
 * "＋ Add to sourcing" — the user-initiated entry point that puts a stone on the
 * buyer's Sourcing list (docs/ASKA-PLUS-AGENTIC-SOURCING.md §2). It sits next to
 * the favourite ❤️ everywhere a stone appears (chat card, browse card/table,
 * the stone modal). Favourites (save-for-later) and the Sourcing list
 * (go-get-it) are two distinct verbs — this is the go-get-it one, a "+" that
 * fills to a "✓" when the stone is on the list.
 *
 * ⚠️ FLAG-GATED. Renders NOTHING unless `canUseAgenticSourcing()` — so on prod
 * (server flag off, no VITE var, no `?sourcing=1` latch) this button does not
 * exist and the whole feature is inert. A preview flips it on. It watches the
 * boot hydration broadcast so a flag that arms mid-session reveals the buttons
 * without a reload.
 *
 * The toggle writes the CLIENT list only (`lib/sourcingList`); nothing is sent
 * to the server here — the tray's "Start sourcing" is the (still inert) place.
 */
export function AddToSourcingButton({
  stone,
  size = 14,
  className = '',
  testId = 'add-to-sourcing',
}: {
  stone: Diamond
  size?: number
  className?: string
  testId?: string
}) {
  // The flag can arm mid-session (boot hydration fires aska:sourcing-changed);
  // re-read on that so the buttons appear without a reload.
  const [armed, setArmed] = useState<boolean>(() => canUseAgenticSourcing())
  useEffect(() => {
    const onChange = () => setArmed(canUseAgenticSourcing())
    window.addEventListener('aska:sourcing-changed', onChange)
    return () => window.removeEventListener('aska:sourcing-changed', onChange)
  }, [])

  const key = stoneKey(stone)
  const [inList, setInList] = useState<boolean>(() => isInSourcing(key))
  useEffect(() => {
    const sync = () => setInList(isInSourcing(key))
    sync()
    return subscribeSourcing(sync)
  }, [key])

  if (!armed) return null

  const toggle = () => {
    const next = !inList
    feedback.toggle(next)
    if (next) addToSourcing(stone)
    else removeFromSourcing(key)
  }

  const label = inList
    ? `Remove ${stone.stockId} from your sourcing list`
    : `Add ${stone.stockId} to your sourcing list`

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggle() }}
      // Stop a mobile row's tap-and-hold timer arming under this press.
      onPointerDown={(e) => e.stopPropagation()}
      aria-pressed={inList}
      aria-label={label}
      title={inList ? 'On your sourcing list' : 'Add to sourcing list'}
      data-testid={testId}
      data-active={inList || undefined}
      className={`inline-flex shrink-0 items-center justify-center rounded-full border transition-colors disabled:opacity-40 ${
        inList
          ? 'border-gold bg-gold/10 text-gold'
          : 'border-line text-navy-500 hover:border-gold/50 hover:text-gold'
      } ${className || 'h-7 w-7'}`}
    >
      {inList ? (
        // On the list — a check.
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : (
        // Not yet — a plus.
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 5v14M5 12h14" />
        </svg>
      )}
    </button>
  )
}
